import {
  ChangeEvent,
  MouseEvent,
  MutableRefObject,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";

import { AutocompleteOptionsList } from "chameleon/lib/components/dropdown-autocomplete/autocomplete-options-list";
import {
  PoweredByFooter,
  PoweredByType,
} from "chameleon/lib/components/dropdown-autocomplete/powered-by-footer";
import { DropdownBehaviour } from "chameleon/lib/components/dropdown-behaviour/dropdown-behaviour";
import { InlineMessage } from "chameleon/lib/components/inline-message/inline-message";
import { InputTitle } from "chameleon/lib/components/input-title/input-title";

import {
  inputCoreClass,
  inputErrorClass,
  inputTypographyClass,
  inputWrapperClass,
} from "../input/input.css";
import {
  dropdownContainerClass,
  dropdownContainerInputClass,
  dropdownContainerUlClass,
} from "./dropdown-autocomplete.css";
import { useKeyboardNavigation } from "./use-keyboard-navigation";

export type AutocompleteOptionType = {
  value: string;
  title: string;
  prediction?: google.maps.places.AutocompletePrediction;
};

export interface DropdownAutocompleteProps {
  id: string;
  ariaLabel?: string;
  ariaLabelDataList?: string;
  captionMessage?: string;
  errorMessage?: string;
  errorMessageKey?: string;
  info?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  onBlur?: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelectOption?: (option: AutocompleteOptionType) => void;
  onFocus?: () => void;
  placeholder?: string;
  title?: string;
  maxLength?: number;
  options?: AutocompleteOptionType[];
  value?: string;
  tabIndex?: number;
  loading?: boolean;
  poweredBy?: PoweredByType;
}

/**
 * Dropdown Autocomplete component for search suggestion. Accepts a forwarded Ref!
 */
export const DropdownAutocomplete = forwardRef<
  HTMLInputElement,
  DropdownAutocompleteProps
>(
  (
    {
      ariaLabel = "autocomplete",
      isDisabled = false,
      isRequired = false,
      captionMessage,
      id,
      info,
      maxLength = 50,
      onFocus,
      onBlur,
      onChange,
      onSelectOption,
      placeholder,
      title,
      value,
      tabIndex,
      options = [],
      errorMessage,
      errorMessageKey,
      loading = false,
      poweredBy,
    }: DropdownAutocompleteProps,
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [cursor, setCursor] = useState(0);
    const [visible, setVisible] = useState(false);

    const handleSelectItem = (
      e: MouseEvent<HTMLLIElement>,
      option: AutocompleteOptionType
    ) => {
      e.preventDefault();
      if (onSelectOption) onSelectOption(option);
      setVisible(false);
    };

    const handleFocus = () => {
      if (options.length > 0) {
        setVisible(true);
      }
      if (onFocus) onFocus();
    };

    const handleBlur = () => {
      setVisible(false);
      if (onBlur) onBlur();
    };

    const handleMouseOver = (index: number) => {
      setCursor(index);
    };

    useEffect(() => {
      const isInputFocused =
        inputRef.current?.id === document.activeElement?.id;
      setVisible(options.length > 0 && isInputFocused);
    }, [options]);

    const keyDownEventHandler = useKeyboardNavigation({
      options,
      cursor,
      setCursor,
      onSelectOption,
      setVisible,
    });

    const hasMessage = captionMessage || errorMessage;
    const captionID = `${id}-${errorMessageKey}-caption`;
    const errorStyles = errorMessage ? inputErrorClass : "";

    return (
      <div className={inputWrapperClass}>
        {title && <InputTitle info={info} htmlFor={id} title={title} />}
        <input
          aria-required={isRequired}
          aria-label={ariaLabel}
          aria-autocomplete="list"
          className={`${inputCoreClass} ${dropdownContainerInputClass} ${errorStyles} ${inputTypographyClass} validateField`}
          disabled={isDisabled}
          type="text"
          placeholder={placeholder}
          id={id}
          name={id}
          role="textbox"
          maxLength={maxLength}
          onBlur={handleBlur}
          onChange={onChange}
          onFocus={handleFocus}
          value={value}
          ref={(element: HTMLInputElement) => {
            (inputRef as MutableRefObject<HTMLInputElement>).current = element;
            if (ref) {
              (ref as MutableRefObject<HTMLInputElement>).current = element;
            }
          }}
          tabIndex={tabIndex}
          autoComplete="off"
          onKeyDown={keyDownEventHandler}
          data-testid={id}
        />
        <DropdownBehaviour isOpen={visible} setIsOpen={setVisible} ref={ref}>
          <div
            aria-label={`${ariaLabel}-options`}
            tabIndex={-1}
            className={dropdownContainerClass}
          >
            <ul
              role="listbox"
              tabIndex={0}
              aria-label="dropdown"
              className={dropdownContainerUlClass}
            >
              <AutocompleteOptionsList
                options={options}
                loading={loading}
                cursor={cursor}
                handleMouseOver={handleMouseOver}
                handleSelectItem={handleSelectItem}
                id={id}
              />
            </ul>
            <PoweredByFooter poweredBy={poweredBy} />
          </div>
        </DropdownBehaviour>
        {hasMessage && (
          <InlineMessage
            id={captionID}
            message={errorMessage || captionMessage}
            type={errorMessage ? "error" : "info"}
          />
        )}
      </div>
    );
  }
);

DropdownAutocomplete.displayName = "DropdownAutocomplete";
